import React, { useState } from "react";
import Grain from "../Grain/Grain";
import Reveal from "../Reveal/Reveal";
import { WHATSAPP_URL } from "../../Constants/constants";
import { BILLING_INTERVALS, BILLING_INTERVAL_OPTIONS, PLAN_KINDS } from "../../data/plans";
import { useAppLinks } from "../../hooks/useAppLinks";
import { PRICES_STATUS, usePlanPrices } from "../../hooks/usePlanPrices";
import { formatPrice } from "../../utils/formatPrice";
import "./styles.css";

const PRICE_STATUS = {
  LOADING: "loading",
  UNAVAILABLE: "unavailable",
  READY: "ready",
};

const resolvePaidPrice = (formatted, pricesStatus, suffix) => {
  if (formatted) return { status: PRICE_STATUS.READY, label: formatted, suffix };
  if (pricesStatus === PRICES_STATUS.LOADING) return { status: PRICE_STATUS.LOADING };
  return { status: PRICE_STATUS.UNAVAILABLE };
};

// Cada CTA é só um link: identidade e pagamento são resolvidos pelo app.
const resolvePlan = (plan, { billingInterval, suffix, prices, pricesStatus, links }) => {
  switch (plan.kind) {
    case PLAN_KINDS.FREE:
      return {
        href: links.signup,
        price: { status: PRICE_STATUS.READY, label: "R$ 0", suffix },
      };
    case PLAN_KINDS.ENTERPRISE:
      return {
        href: WHATSAPP_URL,
        external: true,
        price: { status: PRICE_STATUS.READY, label: "Sob consulta" },
      };
    default: {
      const planoChave = plan.planKeys[billingInterval];
      const preco = prices[planoChave];
      if (billingInterval !== BILLING_INTERVALS.ANNUAL || !preco) {
        return { href: links.subscribe(planoChave), price: resolvePaidPrice(formatPrice(preco), pricesStatus, suffix) };
      }
      // Anual: equivalente mensal em destaque e o total cobrado logo abaixo.
      const mensal = formatPrice({ ...preco, valor_centavos: Math.round(preco.valor_centavos / 12) });
      const price = resolvePaidPrice(mensal, pricesStatus, suffix);
      if (price.status === PRICE_STATUS.READY) price.note = `Cobrado ${formatPrice(preco)} por ano`;
      return { href: links.subscribe(planoChave), price };
    }
  }
};

// Desconto do anual sobre 12 mensalidades. Vale o menor entre os planos pagos:
// nunca anuncia mais do que o plano menos descontado dá.
const annualDiscount = (plans, prices) => {
  const percents = plans
    .filter((plan) => plan.kind === PLAN_KINDS.PAID)
    .map((plan) => {
      const monthly = prices[plan.planKeys[BILLING_INTERVALS.MONTHLY]]?.valor_centavos;
      const annual = prices[plan.planKeys[BILLING_INTERVALS.ANNUAL]]?.valor_centavos;
      return monthly && annual ? Math.round((1 - annual / (monthly * 12)) * 100) : null;
    });
  if (!percents.length || percents.includes(null)) return null;

  const percent = Math.min(...percents);
  return percent > 0 ? percent : null;
};

const renderPrice = ({ status, label, suffix }) => {
  if (status === PRICE_STATUS.LOADING) {
    return <span className="price-skeleton" role="status" aria-label="Carregando preço" />;
  }
  if (status === PRICE_STATUS.UNAVAILABLE) {
    return <span className="price-unavailable">Valor indisponível no momento</span>;
  }
  return (
    <>
      <span className="amount">{label}</span>
      {suffix && <small>{suffix}</small>}
    </>
  );
};

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const Pricing = ({ plans }) => {
  const [billingInterval, setBillingInterval] = useState(BILLING_INTERVALS.MONTHLY);
  const { status: pricesStatus, prices } = usePlanPrices();
  const links = useAppLinks();

  const { suffix } = BILLING_INTERVAL_OPTIONS.find((option) => option.id === billingInterval);
  const discount = annualDiscount(plans, prices);

  return (
    <section className="pricing paper" id="pricing">
      <Grain variant="paper" />
      <div className="container">
        <Reveal className="section-head">
          <span className="tag">Investimento</span>
          <h2 className="display">Planos que acompanham você</h2>
        </Reveal>

        <Reveal className="toggle" role="group" aria-label="Periodicidade de cobrança">
          {BILLING_INTERVAL_OPTIONS.map((option) => (
            <button
              key={option.id}
              type="button"
              className={billingInterval === option.id ? "active" : ""}
              aria-pressed={billingInterval === option.id}
              onClick={() => setBillingInterval(option.id)}
            >
              {option.label}
              {option.id === BILLING_INTERVALS.ANNUAL && discount && (
                <span className="off">{discount}% off</span>
              )}
            </button>
          ))}
        </Reveal>

        <Reveal as="p" className="plans-note">
          Todos os planos incluem pedido cirúrgico e recurso técnico de glosa.
        </Reveal>

        <div className="plans">
          {plans.map((plan, i) => {
            const { href, external, price } = resolvePlan(plan, {
              billingInterval,
              suffix,
              prices,
              pricesStatus,
              links,
            });

            return (
              <Reveal className={`plan ${plan.mostPopular ? "featured" : ""}`} delay={i} key={plan.id}>
                {plan.mostPopular && <span className="badge">Mais popular</span>}
                <h3>{plan.name}</h3>
                <div className="price">{renderPrice(price)}</div>
                {price.note && <p className="price-note">{price.note}</p>}
                <ul>
                  {plan.permissions.map((permission) => (
                    <li key={permission}>
                      <CheckIcon />
                      {permission}
                    </li>
                  ))}
                </ul>
                <a
                  className={`btn ${plan.mostPopular ? "btn-primary" : "btn-ink"}`}
                  href={href}
                  {...(external && { target: "_blank", rel: "noopener noreferrer" })}
                >
                  {plan.ctaLabel}
                </a>
              </Reveal>
            );
          })}
        </div>

        {billingInterval === BILLING_INTERVALS.ANNUAL && (
          <p className="plans-terms">
            No anual, a cobrança é feita uma vez por ano. Você pode desistir com reembolso integral
            em até 7 dias após a cobrança; depois disso, o cancelamento vale no fim do período, sem
            reembolso proporcional.
          </p>
        )}
      </div>
    </section>
  );
};

export default Pricing;
